import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Sidebar from './Sidebar';
import { useParams, useLocation } from 'react-router-dom';
import { Session } from "./utils/session";
import '../styles.css';
import { API_BASE_URL } from '../config';

const authHeaders = () => {
  const token = Session.getToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

function formatFullDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export default function ViewActivityHistory() {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const { id } = useParams();
  const { state } = useLocation();
  const incomingWorkDate = state?.workDate || null;

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workDate, setWorkDate] = useState(incomingWorkDate);
  const [modalImage, setModalImage] = useState(null);
  const [imageError, setImageError] = useState(false);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    } else {
      setHideDesktop((s) => !s);
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) overlay.style.display = showMobileSidebar ? 'block' : 'none';

    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      sidebar.classList.toggle('show-mobile', showMobileSidebar);
      sidebar.classList.toggle('hide-desktop', hideDesktop);
    }
    const content = document.getElementById('content');
    if (content) content.classList.toggle('full-width', hideDesktop);
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
    fetchActivityHistory();
  }, []);
  const fetchActivityHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const dateObj = workDate ? new Date(workDate) : new Date();
      const dateStr = dateObj.toISOString().slice(0, 10);

      const API_URL = `${API_BASE_URL}/api/ViewActivityHistory/ViewActivityHistoryByUserId?userId=${encodeURIComponent(id)}&date=${encodeURIComponent(dateStr)}`;

      const res = await fetch(API_URL, { headers: authHeaders() });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        if (res.status === 404) {
          console.warn('Activity history not found for', id, dateStr, txt);
          setEntries([]);
          return;
        }
        throw new Error(`API error ${res.status}: ${txt}`);
      }

      const data = await res.json().catch(() => null);

      if (data) {
        if (Array.isArray(data)) {
          setEntries(data);
        } else if (Array.isArray(data.data)) {
          setEntries(data.data);
        } else if (Array.isArray(data.result)) {
          setEntries(data.result);
        } else if (Array.isArray(data.items)) {
          setEntries(data.items);
        } else {
          setEntries([]);
        }
      } else {
        setEntries([]);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load activity history.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [id, workDate]);


  const mappedEntries = useMemo(() => {
    const mapped = (entries || []).map((dto) => {
      const snaps = Array.isArray(dto.snaps) ? dto.snaps : [];
   
const computeClicks = (s) => {
  const totalField = s.TotalClicks ?? s.totalClicks;

  if (typeof totalField === 'number') {
    return totalField;
  }

  const mouse = Number(s.MouseClicks ?? s.mouseClicks ?? 0) || 0;
  const keyboard = Number(s.KeyboardClicks ?? s.keyboardClicks ?? 0) || 0;

  return mouse + keyboard;
};

const IDLE_THRESHOLD = 5;
const MAX_THRESHOLD = 100;

const clicksToPct = (clicks) => {
  const n = Number(clicks) || 0;

  if (n <= 10) return 0;
  if (n >= 100) return 100;

  if (n <= 49) {
    return Math.round(((n - 6) / (49 - 6)) * 50);
  }

  // Map 50-99 to 50-99 (linear scale to 50-100)
  return Math.round(50 + ((n - 50) / (99 - 50)) * 50);
};

const makeUrl = (p) => {
  if (!p) return '';

  const str = String(p).trim();
  if (!str) return '';

  if (/^https?:\/\//i.test(str) || /^\/\//.test(str)) {
    return str;
  }

  if (!API_BASE_URL) return str;

  const base = String(API_BASE_URL).replace(/\/$/, '');
  const path = str.replace(/^\//, '');

  return `${base}/${encodeURI(path)}`;
};

const thumbs = snaps.map((s) => {
  const snapRaw =
    s.SnapOn ??
    s.snapOn ??
    s.SnapOnTime ??
    s.snapOnTime ??
    null;

  const snapDate = snapRaw ? new Date(snapRaw) : null;

  const isValidDate =
    snapDate instanceof Date && !isNaN(snapDate.getTime());

  const timeLabel = isValidDate
    ? snapDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    : snapRaw ?? '';

  const clicks = computeClicks(s);
  const pct = clicksToPct(clicks);

  const thumbPath =
    s.snapThumbnailPath ??
    s.snapThumbnail ??
    s.snapPath ??
    s.snap ??
    null;

  const fullPath =
    s.snapPath ??
    s.snap ??
    s.snapThumbnailPath ??
    s.snapThumbnail ??
    null;

  return {
    thumb: makeUrl(thumbPath),
    full: makeUrl(fullPath),
    time: timeLabel,
    pct,
    clicks,
    mouseClicks: Number(s.MouseClicks ?? s.mouseClicks ?? 0) || 0,
    keyboardClicks: Number(s.KeyboardClicks ?? s.keyboardClicks ?? 0) || 0,
    snapRaw,
    id: s.UserSnapId ?? s.userSnapId ?? null,
    taskName: s.TaskName ?? s.taskName ?? ''
  };
});

      const originalRange = dto.TimeRange || dto.timeRange || '';

      const parseSnapTime = (s) => {
        if (!s) return null;
        if (s.SnapOn) {
          const d = new Date(s.SnapOn);
          if (!isNaN(d)) return d;
        }
        const t = s.SnapOnTime || s.snapOnTime || s.SnapOn || s.snapOn;
        if (!t) return null;

        const pd = new Date(t);
        if (!isNaN(pd)) return pd;

        const parts = String(t).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?/);
        if (parts) {
          let hh = parseInt(parts[1], 10);
          const mm = parseInt(parts[2], 10);
          const ss = parts[3] ? parseInt(parts[3], 10) : 0;
          const ampm = parts[4];
          if (ampm && /am/i.test(ampm) && hh === 12) hh = 0;
          if (ampm && /pm/i.test(ampm) && hh < 12) hh += 12;
          const now = new Date();
          return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm, ss);
        }
        return null;
      };

      const snapDates = snaps.map(parseSnapTime).filter(Boolean);
      let displayRange = originalRange;
      if (snapDates.length > 0) {
        const minT = new Date(Math.min(...snapDates.map((d) => d.getTime())));
        const maxT = new Date(Math.max(...snapDates.map((d) => d.getTime())));
        const startStr = minT.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        const endStr = maxT.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

        const diffMinutes = Math.max(0, Math.round((maxT.getTime() - minT.getTime()) / 60000));
        const hrs = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;
        const durationStr = ` (${hrs}:${mins.toString().padStart(2, '0')} hrs)`;

        displayRange = `${startStr} - ${endStr}${durationStr}`;
      }

      return {
        range: displayRange,
        title: dto.MachineName || dto.machineName || `Activity (${dto.UserTrackingId ?? dto.userTrackingId ?? ''})`,
        thumbs,
        raw: dto
      };
    });
    let summedMinutes = 0;
    mapped.forEach((e) => {
      const m = (e.range || '').match(/\((\d+):(\d{2})\s*hrs?\)/);
      if (m) {
        const h = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        summedMinutes += h * 60 + mm;
      }
    });
    const hours = Math.floor(summedMinutes / 60);
    const mins = summedMinutes % 60;
    return { mapped, totalTimeStr: `${hours}:${mins.toString().padStart(2, '0')}` };
  }, [entries]);

  useEffect(() => {
    const contentEl = document.getElementById('content');
    if (contentEl && !contentEl.style.position) contentEl.style.position = 'relative';
  }, []);

  const openImage = useCallback((index, images, caption) => {
    setModalImage({ index, images, caption });
    setImageError(false);
    document.body.style.overflow = 'hidden';
  }, []);
  const closeModal = useCallback(() => {
    setModalImage(null);
    setImageError(false);
    document.body.style.overflow = '';
  }, []);

  const prevImage = useCallback(() => {
    if (!modalImage) return;
    setModalImage((prev) => ({
      ...prev,
      index: (prev.index - 1 + prev.images.length) % prev.images.length
    }));
    setImageError(false);
  }, [modalImage]);

  const nextImage = useCallback(() => {
    if (!modalImage) return;
    setModalImage((prev) => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length
    }));
    setImageError(false);
  }, [modalImage]);

  useEffect(() => {
    const onKey = (e) => {
      if (!modalImage) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalImage, closeModal, prevImage, nextImage]);

  const summaryStyle = {
    background: '#fff',
    borderRadius: 8,
    padding: '18px 20px',
    boxShadow: 'none',
    border: '1px solid #eef1f4'
  };

  const thumbContainer = {
    borderRadius: 6,
    overflow: 'hidden',
    background: '#f8f9fa',
    border: '1px solid #eef1f4'
  };

  function ActivityEntry({ entry, openImage }) {
    const [selected, setSelected] = useState(0);
    const [mainLoaded, setMainLoaded] = useState(false);
    const [mainError, setMainError] = useState(false);

    const images = (entry.thumbs || []).map((t) => t.full || t.thumb || '');
    const mainThumb = (entry.thumbs && entry.thumbs[selected]) ? (entry.thumbs[selected].full || entry.thumbs[selected].thumb) : (images[0] || '');

    useEffect(() => {
      setMainLoaded(false);
      setMainError(false);
      if (!mainThumb) return undefined;
      const img = new Image();
      img.onload = () => setMainLoaded(true);
      img.onerror = () => setMainError(true);
      img.src = mainThumb;
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [mainThumb]);

    const handleThumbClick = (i) => {
      setSelected(i);
      // open modal with the full image for the clicked thumbnail
      openImage(i, images, entry.thumbs && entry.thumbs[i] ? entry.thumbs[i].taskName : undefined);
    };

    return (
      <div>
    
        <div className="d-md-none">
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
            {entry.thumbs.map((t, ti) => (
              <div key={ti} style={{ minWidth: 120, maxWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <div style={thumbContainer}>
                    <img src={t.thumb || t.full} alt={t.time} style={{ width: '100%', height: 70, objectFit: 'cover', cursor: 'pointer', border: ti === selected ? '2px solid #0084fe' : 'none' }} onClick={() => handleThumbClick(ti)} />
                  </div>
                </div>
                <div style={{ marginTop: 6, maxWidth: 120, textAlign: 'center' }}>
                  <div style={{ height: 8, width: '100%', background: '#e9ecef', borderRadius: 6, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ width: `${t.pct || 0}%`, height: '100%', background: (t.pct || 0) > 0 ? '#28a745' : '#cfd8dc', transition: 'width .25s ease' }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>{t.time || ''}</div>
                  <div title={t.taskName || ''} style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{t.taskName || ''}</div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>{typeof t.clicks !== 'undefined' ? `${t.clicks} clicks` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="row gx-3 gy-3 d-none d-md-flex">
          {entry.thumbs.map((t, ti) => (
            <div key={ti} className="col-auto" style={{ width: 120, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <div style={thumbContainer}>
                  <img src={t.thumb || t.full} alt={t.time} style={{ width: '100%', height: 60, objectFit: 'cover', cursor: 'pointer', border: ti === selected ? '2px solid #0084fe' : 'none' }} onClick={() => handleThumbClick(ti)} />
                </div>
              </div>
              <div style={{ marginTop: 6, maxWidth: 120, textAlign: 'center' }}>
                <div style={{ height: 8, width: '100%', background: '#e9ecef', borderRadius: 6, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ width: `${t.pct || 0}%`, height: '100%', background: (t.pct || 0) > 0 ? '#28a745' : '#cfd8dc', transition: 'width .25s ease' }} />
                </div>
                
                <div title={t.taskName || ''} style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{t.taskName || ''}</div>
                <div style={{ fontSize: 12, color: '#6c757d' }}>{t.time || ''}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar"><Sidebar /></div>
      <div id="content">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            {/* <button type="button" id="sidebarCollapse" className="btn btn-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
              <i className="fas fa-bars" />
            </button> */}
            <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
            <div><h5 className="mb-0">{formatFullDate(workDate)}</h5></div>
          </div>
        </div>

        {/* Summary card */}
        <div style={summaryStyle} className="mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
            <div>
              <div className="d-flex align-items-center gap-3">
                <div style={{ fontSize: 28, fontWeight: 600 }}>Total: {mappedEntries.totalTimeStr}</div>
                <button type="button" className="btn btn-link p-0" title={loading ? 'Refreshing...' : 'Refresh total'}
                  onClick={fetchActivityHistory} disabled={loading}                  >
                  <i className={`fas fa-sync-alt text-muted ${loading ? 'fa-spin' : ''}`} /></button>
              </div>
              <div className="text-muted small mt-1">Detailed tracked time for the selected day</div>
            </div>
            <div className="d-flex align-items-center gap-4 mt-3 mt-md-0">
              <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#28a745', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Tracked</div>
                </div>
                <div className="fw-bold">{mappedEntries.totalTimeStr}</div>
              </div>
              {/* <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#0dcaf0', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Manual</div>
                </div>
                <div className="fw-bold">—</div>
              </div>
              <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#dc3545', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Overtime</div>
                </div>
                <div className="fw-bold">—</div>
              </div> */}
            </div>
          </div>
        </div>

        <div>
          {mappedEntries.mapped.map((entry, ei) => (
            <div key={ei} className="mb-5">
              <div className="d-flex align-items-start justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ width: 12, height: 12, background: '#28a745', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">{entry.range}</div>
                </div>
                <div>
                  <button className="btn btn-sm btn-light rounded-circle"><i className="fas fa-ellipsis-h text-muted" /></button>
                </div>
              </div>
              <div className="mb-3"><div className="fw-semibold">{entry.title}</div></div>
              <div className="mb-3">
                <ActivityEntry entry={entry} openImage={(idx, images, caption) => openImage(idx, images, caption)} />
              </div>
              <hr />
            </div>
          ))}
        </div>
      </div>
      {modalImage && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            overflow: 'auto',
            padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <button
              onClick={closeModal}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 5,
                background: '#0084fe',
                color: '#fff',
                border: 'none',
                padding: '5px 10px',
                borderRadius: 6,
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
            {modalImage.index > 0 && (
              <button
                onClick={prevImage}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  zIndex: 5,
                  background: '#0084fe',
                  color: '#fff',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                ‹
              </button>
            )}
            {modalImage.index < modalImage.images.length - 1 && (
              <button
                onClick={nextImage}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  zIndex: 5,
                  background: '#0084fe',
                  color: '#fff',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                ›
              </button>
            )}
            {modalImage.images[modalImage.index] && !imageError ? (
              <img
                src={modalImage.images[modalImage.index]}
                alt={`Preview ${modalImage.index + 1}`}
                onError={() => setImageError(true)}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '95vh',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div
                style={{
                  width: '82.4vw',
                  height: '95vh',
                  background: 'rgb(215 205 205)',
                  color: '#343a40',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: 16,
                  borderRadius: 6
                }}
              >
                No Image Available.
              </div>
            )}

            {modalImage.caption && (
              <div style={{ color: '#fff', marginTop: 8, textAlign: 'center', fontSize: 14 }}>
                {modalImage.caption}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
